import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { type AutomationActivityItem, getAutomationActivity } from '../lib/api.js';
import { formatRelative } from '../lib/format.js';
import { LIVE_POLL_MS } from '../lib/poll.js';
import { HistoryFacets } from './ui/HistoryFacets.js';

/**
 * B3 — the Automation log: a read-only, newest-first feed of what the AI did
 * AUTOMATICALLY (auto-congrats, auto-respond, keep-in-touch greetings, and — when
 * armed — auto-engagement / auto-invitation acts). The transparency surface for the
 * gated auto paths now that they run without review. No per-row undo: auto-sends are
 * irreversible, so this is the honest record, not a control panel.
 */
const KIND_LABEL: Record<string, string> = {
  job_change_congrats: 'Job-change congrats',
  auto_reply: 'Auto-reply',
  keep_in_touch: 'Keep-in-touch',
  engagement: 'LinkedIn engagement',
  invitation: 'Connection request',
};

function statusTone(status: string, error: boolean): 'ok' | 'warn' | 'muted' {
  if (error || status === 'failed') return 'warn';
  if (status === 'sent' || status === 'done') return 'ok';
  return 'muted';
}

// Wave-2 — the log UNIONs five audit tables with DIFFERENT success vocab ('sent'
// for congrats/auto-reply/keep-in-touch/engagement, 'done' for invitations), so
// the Status facet buckets into Completed / Failed; an in-flight 'attempted' row
// matches neither chip (it only shows under "All").
function statusBucket(it: AutomationActivityItem): 'ok' | 'failed' | 'other' {
  if (it.error || it.status === 'failed') return 'failed';
  if (it.status === 'sent' || it.status === 'done') return 'ok';
  return 'other';
}

export function AutomationActivityView() {
  const q = useQuery({
    queryKey: ['automation-activity'],
    queryFn: ({ signal }) => getAutomationActivity({ signal }),
    refetchInterval: LIVE_POLL_MS,
  });
  const items = q.data?.items ?? [];
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter(
      (it) =>
        (!query ||
          (it.contact_name ?? '').toLowerCase().includes(query) ||
          it.action.toLowerCase().includes(query)) &&
        (!kind || it.kind === kind) &&
        (!status || statusBucket(it) === status),
    );
  }, [items, search, kind, status]);
  // Stable, collision-proof React keys WITHOUT the array index (biome
  // noArrayIndexKey): the feed is PII-safe and carries no id, so derive a key
  // from the row's content and disambiguate genuine duplicates (same instant +
  // kind + action + contact) with a per-content occurrence counter.
  const seen = new Map<string, number>();
  const keyed = filtered.map((it) => {
    const base = `${it.at}|${it.kind}|${it.action}|${it.contact_name ?? ''}|${it.status}|${it.error ? 'e' : ''}`;
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return { it, key: n === 0 ? base : `${base}#${n}` };
  });
  return (
    <div className="autolog-view" data-testid="automation-activity">
      <header className="autolog-head">
        <h1 className="autolog-title">Automation log</h1>
        <p className="autolog-sub">
          What the AI did automatically, newest first. Auto-sends can't be undone — this is the
          record so nothing runs silently.
        </p>
      </header>
      {q.isError ? (
        <p className="autolog-empty">Couldn't load the automation log.</p>
      ) : items.length === 0 ? (
        <p className="autolog-empty">No automated actions yet.</p>
      ) : (
        <>
          <HistoryFacets
            search={search}
            onSearch={setSearch}
            placeholder="Search by name or action…"
            shown={filtered.length}
            total={items.length}
            groups={[
              {
                label: 'Type',
                value: kind,
                onChange: setKind,
                options: [
                  { value: 'job_change_congrats', label: 'Congrats' },
                  { value: 'auto_reply', label: 'Auto-reply' },
                  { value: 'keep_in_touch', label: 'Keep-in-touch' },
                  { value: 'engagement', label: 'Engagement' },
                  { value: 'invitation', label: 'Requests' },
                ],
              },
              {
                label: 'Status',
                value: status,
                onChange: setStatus,
                options: [
                  { value: 'ok', label: 'Completed' },
                  { value: 'failed', label: 'Failed' },
                ],
              },
            ]}
          />
          {keyed.length === 0 ? (
            <p className="autolog-empty">No matches.</p>
          ) : (
            <ul className="autolog-list">
              {keyed.map(({ it, key }) => (
                <AutoRow key={key} it={it} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function AutoRow({ it }: { it: AutomationActivityItem }) {
  const tone = statusTone(it.status, it.error);
  return (
    <li className="autolog-row" data-testid="autolog-row">
      <span className={`autolog-dot tone-${tone}`} aria-hidden="true" />
      <span className="autolog-main">
        <span className="autolog-action">
          {it.action}
          {it.contact_name ? (
            <>
              {' · '}
              <strong>{it.contact_name}</strong>
            </>
          ) : null}
        </span>
        <span className="autolog-meta">
          {KIND_LABEL[it.kind] ?? it.kind} · {it.error ? 'failed' : it.status}
        </span>
      </span>
      <time className="autolog-time">{formatRelative(it.at)}</time>
    </li>
  );
}
